import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  HR_PILLARS,
  PILLAR_STATE_MAP,
  pillarProgress,
  type HrPillar,
} from '@goyone/shared';
import { Badge, ErrorState, PageHead, Stat } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { api } from '../../lib/api';
import { num, plural, WORDS } from '../../lib/format';

interface HrOverview {
  headcount: number;
  newThisMonth: number;
  onLeaveToday: number;
  openJobs: number;
  pendingRequests: number;
  pendingLeaves: number;
}

/**
 * هيكل الموارد البشرية.
 *
 * عشرُ إداراتٍ تحت مدير الموارد البشرية، كلٌّ منها ببابها ومسؤوليّاتها.
 *
 * ## والحالةُ تُقال ولا تُخفى
 *
 * ما لم يُبنَ يُعرض معطَّلًا مكتوبًا عليه «لم يُبنَ»، لا يُحذف من الشاشة
 * ولا يُعرض بابًا خلفه فراغ. ومن يقرأ الهيكل يريد أن يعرف ما ينقص شركتَه
 * لا أن يُخفى عنه — وإخفاءُ الناقص يجعله يظنّ النظام أقلَّ ممّا يعد به،
 * أو أكثر.
 */
export function HrHome() {
  const overview = useQuery({
    queryKey: ['hr', 'overview'],
    queryFn: () => api<HrOverview>('/hr/overview'),
  });

  const o = overview.data;
  const ready = HR_PILLARS.filter((p) => p.state === 'READY').length;
  const partial = HR_PILLARS.filter((p) => p.state === 'PARTIAL').length;
  const planned = HR_PILLARS.filter((p) => p.state === 'PLANNED').length;

  /*
   * ما لم يُبنَ يُعدّ بالمسؤوليّات لا بالإدارات.
   *
   * الإداراتُ كلُّها صار فيها شاشاتٌ تعمل، والناقصُ مسؤوليّاتٌ بعينها —
   * وقولُ «الباقي مخطَّطٌ ولم تُكتب شيفرتُه» بعد أن لم يبقَ بابٌ مغلقٌ
   * يجعل الصفحةَ تكذب في الاتّجاه الآخر.
   */
  const duties = HR_PILLARS.flatMap((p) => p.duties);
  const undone = duties.filter((d) => !d.done).length;

  return (
    <div className="page">
      <PageHead
        title="الموارد البشرية"
        subtitle="عشر إدارات تحت مدير الموارد البشرية — ما بُني منها وما لم يُبنَ"
      />

      {overview.isError && (
        <ErrorState
          error={overview.error}
          onRetry={() => void overview.refetch()}
        />
      )}

      {overview.isPending && <div className="skeleton" style={{ height: 96 }} />}

      {o && (
        <div className="stats">
          <Stat
            label="على رأس العمل"
            value={num(o.headcount)}
            note={
              o.newThisMonth > 0
                ? `${plural(o.newThisMonth, WORDS.member)} هذا الشهر`
                : undefined
            }
          />
          <Stat
            label="في إجازة اليوم"
            value={num(o.onLeaveToday)}
            note={o.pendingLeaves > 0 ? `${num(o.pendingLeaves)} طلب معلَّق` : undefined}
          />
          <Stat label="وظائف مفتوحة" value={num(o.openJobs)} />
          <Stat label="طلبات تنتظر قرارًا" value={num(o.pendingRequests)} />
        </div>
      )}

      <div className="card">
        <h2 className="card-title">الهيكل</h2>
        <p className="tiny mute">
          {ready + partial} من {HR_PILLARS.length} إدارات فيها شاشاتٌ تعمل
          {planned > 0 ? `، و${num(planned)} مخطَّطٌ لم تُكتب شيفرتُه` : ''}.
          {undone > 0
            ? ` وبقي ${num(undone)} من ${num(duties.length)} مسؤوليّةً لم تُبنَ — تُقال هنا ولا تُخفى.`
            : ' وكلُّ مسؤوليّاتها مبنيّة.'}
        </p>

        <div className="hrp-grid mt-4">
          {HR_PILLARS.map((p) => (
            <PillarCard key={p.key} pillar={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PillarCard({ pillar }: { pillar: HrPillar }) {
  const state = PILLAR_STATE_MAP[pillar.state];
  const progress = pillarProgress(pillar);
  const open = Boolean(pillar.path);

  const body = (
    <>
      <span className="hrp-top">
        <span className={`hrp-icon tone-${state.kind}`} aria-hidden="true">
          <Icon name={pillar.icon} size={18} />
        </span>
        <span className="hrp-name">
          <strong>{pillar.label}</strong>
          <span className="tiny mute" dir="ltr">
            {pillar.labelEn}
          </span>
        </span>
        <Badge kind={state.kind}>{state.label}</Badge>
      </span>

      <p className="tiny mute hrp-purpose">{pillar.purpose}</p>

      {/*
        شريطُ التقدّم يُحسب من المسؤوليّات لا يُكتب يدًا: رقمٌ يُكتب يدًا
        يتخلّف عن الشيفرة بعد أوّل بناء.
      */}
      <span className="hrp-bar" aria-hidden="true">
        <span
          className={`hrp-bar-fill tone-${state.kind}`}
          style={{ width: `${progress.percent}%` }}
        />
      </span>

      <ul className="hrp-duties">
        {pillar.duties.map((d) => (
          <li key={d.label} className={d.done ? 'is-done' : ''}>
            <Icon name={d.done ? 'check' : 'minus'} size={12} />
            {d.label}
          </li>
        ))}
      </ul>

      <span className="tiny mute hrp-foot">
        {progress.done} من {progress.total} مسؤوليّة
        {open ? '' : ' — لا باب بعد'}
      </span>
    </>
  );

  if (!open) {
    return <div className="hrp-card is-planned">{body}</div>;
  }

  return (
    <Link to={pillar.path!} className="hrp-card">
      {body}
    </Link>
  );
}
